import VolumeQuota from './VolumeQuota';
import VolumeUtilisation from './VolumeUtilisation';

// External Imports
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import { PiChartLineLight } from 'react-icons/pi';

import AccessDenied from './AccessDenied';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';

import { fetchCustomer } from '../utils/customer';

export default function Site() {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();
  const params = useParams();

  // Local Data
  const [customer, setCustomer] = useState({});
  const [site, setSite] = useState({});
  const [siteSubscriptions, setSiteSubscriptions] = useState([]);

  const [error, setError] = useState({ message: '', severity: 'primary', access_denied: false });
  const [loading, setLoading] = useState(true);

  const getCustomer = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    fetchCustomer(bearerToken, params.customerId)
      .then((data) => {
        setCustomer(data);

        const subscriptions = data.subscriptions.filter((s) => s.site_id === params.siteId);
        setSiteSubscriptions(subscriptions);

        setError({ message: '', severity: 'success', access_denied: false });
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);

        if (error instanceof ForbiddenError) {
          setError({ message: '', severity: 'danger', access_denied: true });
        } else {
          setError({
            message: 'Could not retrieve customer!',
            severity: 'danger',
            access_denied: false,
          });
        }
      });
  };

  useEffect(() => {
    getCustomer();
  }, [params.customerId]);

  useEffect(() => {
    if (customer.sites != null) {
      const site = customer.sites.find((s) => s.site_id === params.siteId);
      setSite(site);
    }
  }, [customer]);

  return (
    <>
      {error.message != '' ? (
        <Alert variant={error.severity}>
          <span>{error.message}</span>
        </Alert>
      ) : (
        <>
          {error.access_denied ? (
            <AccessDenied />
          ) : (
            <Row>
              <Col>
                <h1 className='text-center'>Site: {params.siteId}</h1>
              </Col>
            </Row>
          )}
        </>
      )}

      {loading ? (
        <Alert variant='warning'>
          <Spinner animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
          <span>Data loading...</span>
        </Alert>
      ) : (
        <>
          <Row className='mb-3'>
            <Col>
              <Card>
                <Card.Header>Details</Card.Header>
                <Card.Body>
                  <Table striped>
                    <tbody>
                      <tr>
                        <th>Customer</th>
                        <td>{customer.name}</td>
                      </tr>
                      <tr>
                        <th>Site ID</th>
                        <td>{site.site_id}</td>
                      </tr>
                      <tr>
                        <th>Site Name</th>
                        <td>{site.name}</td>
                      </tr>
                      {site.asn != null && (
                        <tr>
                          <th>ASN</th>
                          <td>{site.asn}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {customer.subscriptions != null && customer.subscriptions.length > 0 && (
              <Col>
                <Card>
                  <Card.Header>Subnets</Card.Header>
                  <Card.Body>
                    <Table striped>
                      <thead>
                        <tr>
                          <th>Subnet</th>
                          <th>Subscription</th>
                          <th>Pool / Network</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customer.subscriptions
                          .filter((sub) => sub.site_id == params.siteId)
                          .map((sub) => (
                            <tr
                              key={sub.subnet}
                              className={
                                sub.status == 'active'
                                  ? 'table-success'
                                  : sub.status == 'suspended'
                                    ? 'table-warning'
                                    : 'table-danger'
                              }
                            >
                              <th>
                                <a
                                  className='btn btn-primary'
                                  href={
                                    '/customer/' +
                                    params.customerId +
                                    '/bandwidth/subscription/' +
                                    sub.subnet.split('/')[0]
                                  }
                                >
                                  {sub.subnet}
                                </a>
                              </th>
                              <td>{sub.plan_name}</td>
                              {customer.bandwidth_pools.findIndex(
                                (v) => v.pool_name === sub.pool_name,
                              ) > -1 ? (
                                <>
                                  <td>{sub.pool_name} </td>
                                </>
                              ) : (
                                <>
                                  <td>{sub.pool_name}</td>
                                </>
                              )}
                              <td>{sub.status}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Row className='mb-3'>
            <Col>
              <VolumeQuota site={site} subscriptions={siteSubscriptions} />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col>
              <VolumeUtilisation site={site} />
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <Card.Header>Bandwidth and Applications</Card.Header>
                <Card.Body>
                  <p className='text-center'>
                    <a
                      className='btn bi bi-building-exclamation'
                      href={'/customer/' + params.customerId + '/bandwidth/site/' + params.siteId}
                    >
                      View Historical Bandwidth and Applications: <PiChartLineLight size='4em' />
                    </a>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
